import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, X, Users, Skull, UserCircle2, Heart, Sparkles, Star,
  Plus, Filter, ArrowUpDown, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 6 tabs per spec
const TABS = [
  { key: "monsters",   label: "Monsters",   icon: Skull },
  { key: "npcs",       label: "NPCs",       icon: Users },
  { key: "players",    label: "Players",    icon: UserCircle2 },
  { key: "companions", label: "Companions", icon: Heart },
  { key: "summons",    label: "Summons",    icon: Sparkles },
  { key: "recent",     label: "Recent",     icon: Clock },
];

const RECENT_KEY_PREFIX = "vtt_recent_tokens_";
const FAV_KEY_PREFIX    = "vtt_fav_tokens_";

const cleanCR = (cr) => {
  if (cr === undefined || cr === null) return null;
  if (typeof cr === "number") return cr.toString();
  return String(cr);
};

export const TokenBrowserPanel = ({
  open,
  onClose,
  campaignId,
  characters = [],          // PCs
  npcs = [],                // custom NPCs
  creatures = [],           // bestiary creatures
  companions = [],          // characters with character_type === "companion"
  summons = [],             // characters with character_type === "summon"
  onPlaceToken,             // (entity, sourceType, position?) => void
  width: initialWidth = 340,
}) => {
  const [tab, setTab] = useState("monsters");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [filterOpen, setFilterOpen] = useState(false);
  const [crFilter, setCrFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");
  const [favOnly, setFavOnly] = useState(false);

  // Lazy-loaded monsters (paginated; fetch on tab open)
  const [monsters, setMonsters] = useState([]);
  const [monstersLoading, setMonstersLoading] = useState(false);

  // Recents + favs persisted to localStorage per campaign
  const recentKey = `${RECENT_KEY_PREFIX}${campaignId}`;
  const favKey    = `${FAV_KEY_PREFIX}${campaignId}`;
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(recentKey)) || []; } catch { return []; } });
  const [favs, setFavs]   = useState(() => { try { return JSON.parse(localStorage.getItem(favKey)) || []; } catch { return []; } });

  const toggleFav = (key) => {
    setFavs(prev => {
      const next = prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key];
      localStorage.setItem(favKey, JSON.stringify(next));
      return next;
    });
  };
  const noteRecent = (entity, sourceType) => {
    setRecent(prev => {
      const id = `${sourceType}:${entity.id}`;
      const next = [{ id, entity, sourceType, ts: Date.now() }, ...prev.filter(r => r.id !== id)].slice(0, 30);
      localStorage.setItem(recentKey, JSON.stringify(next));
      return next;
    });
  };

  // Width-resize state (persisted)
  const [width, setWidth] = useState(() => parseInt(localStorage.getItem("vtt_token_browser_w"), 10) || initialWidth);
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const move = (ev) => {
      const w = Math.max(280, Math.min(640, startW + (ev.clientX - startX)));
      setWidth(w);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      localStorage.setItem("vtt_token_browser_w", String(width));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Lazy fetch SRD monsters when tab opens
  useEffect(() => {
    if (tab !== "monsters" || monsters.length > 0 || monstersLoading) return;
    setMonstersLoading(true);
    axios.get(`${API}/srd-monsters`, { withCredentials: true })
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.items || []);
        setMonsters(arr);
      })
      .catch(() => {})
      .finally(() => setMonstersLoading(false));
  }, [tab]);

  // Build token list per tab
  const list = useMemo(() => {
    let items = [];
    if (tab === "monsters") items = monsters.map(m => ({ ...m, _src: "monster" }));
    else if (tab === "players") items = characters.map(c => ({ ...c, _src: "character" }));
    else if (tab === "npcs") {
      items = [
        ...npcs.map(n => ({ ...n, _src: "npc" })),
        ...creatures.map(cr => ({ ...cr, _src: "creature" })),
      ];
    }
    else if (tab === "companions") items = companions.map(c => ({ ...c, _src: "character" }));
    else if (tab === "summons") items = summons.map(c => ({ ...c, _src: "character" }));
    else if (tab === "recent") items = recent.map(r => ({ ...r.entity, _src: r.sourceType, _recentTs: r.ts }));

    const q = search.trim().toLowerCase();
    if (q) items = items.filter(it => (it.name || "").toLowerCase().includes(q));
    if (favOnly) {
      items = items.filter(it => favs.includes(`${it._src}:${it.id}`));
    }
    if (tab === "monsters") {
      if (crFilter) items = items.filter(m => cleanCR(m.challenge_rating) === cleanCR(crFilter));
      if (typeFilter) items = items.filter(m => (m.type || m.creature_type || "").toLowerCase() === typeFilter.toLowerCase());
      if (sizeFilter) items = items.filter(m => (m.size || "").toLowerCase() === sizeFilter.toLowerCase());
    }
    items.sort((a, b) => {
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      if (sort === "cr") return (parseFloat(a.challenge_rating) || 0) - (parseFloat(b.challenge_rating) || 0);
      if (sort === "recent") return (b._recentTs || 0) - (a._recentTs || 0);
      return 0;
    });
    return items;
  }, [tab, monsters, characters, npcs, creatures, companions, summons, recent, search, favOnly, favs, crFilter, typeFilter, sizeFilter, sort]);

  // ---- Drag handlers ----
  const handleDragStart = (e, item) => {
    const payload = { id: item.id, name: item.name, _src: item._src, image_path: item.image_path || item.token_image, hit_points: item.hit_points || item.hp, armor_class: item.armor_class || item.ac, raw: item };
    e.dataTransfer.setData("application/x-natone-token", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };

  const quickSpawn = (item) => {
    onPlaceToken?.(item, item._src);
    noteRecent(item, item._src);
  };

  if (!open) return null;

  return (
    <div
      className="absolute top-12 left-14 z-30 bg-[#0f0f12]/95 backdrop-blur-md border border-[#333] rounded-r-lg shadow-2xl flex flex-col"
      style={{ width, height: "calc(100% - 4rem)" }}
      onWheel={(e) => e.stopPropagation()}
      data-vtt-sidebar="tokens"
      data-testid="token-browser-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#333] bg-gradient-to-b from-[#1a1a22] to-[#101015] rounded-tr-lg">
        <h3 className="text-base font-bold text-white flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" /> Token Browser</h3>
        <button onClick={onClose} className="text-[#888] hover:text-white" data-testid="close-token-browser"><X className="w-4 h-4" /></button>
      </div>

      {/* Tabs — horizontally scrollable so labels never collide */}
      <div className="flex border-b border-[#333] bg-[#0a0a0d] overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 min-w-[72px] py-2 px-2 text-xs uppercase tracking-wider flex flex-col items-center gap-1 transition-colors whitespace-nowrap font-medium ${active ? "bg-primary/20 text-primary border-b-2 border-primary" : "text-[#aaa] hover:text-white"}`}
              data-testid={`tb-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search + filter + sort */}
      <div className="p-2 border-b border-[#333] space-y-2">
        <div className="flex gap-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${tab}…`}
              className="h-8 text-sm pl-7 bg-[#1a1a1a] border-[#333] text-white"
              data-testid="tb-search"
            />
          </div>
          {tab === "monsters" && (
            <Button variant="ghost" size="sm" onClick={() => setFilterOpen(o => !o)} className={`h-8 px-2 ${filterOpen ? "text-primary" : "text-[#888]"}`} data-testid="tb-filter-toggle"><Filter className="w-3.5 h-3.5" /></Button>
          )}
          <button
            onClick={() => setFavOnly(v => !v)}
            className={`h-8 px-2 flex items-center text-xs rounded-sm border ${favOnly ? "border-yellow-500 text-yellow-400" : "border-[#333] text-[#888]"}`}
            title="Favorites only"
            data-testid="tb-fav-toggle"
          >
            <Star className={`w-3.5 h-3.5 ${favOnly ? "fill-yellow-400" : ""}`} />
          </button>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <ArrowUpDown className="w-3 h-3 text-[#666]" />
          <span className="text-[#666]">Sort:</span>
          {["name", ...(tab === "monsters" ? ["cr"] : []), ...(tab === "recent" ? ["recent"] : [])].map(s => (
            <button key={s} onClick={() => setSort(s)} className={`px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${sort === s ? "bg-primary/20 text-primary" : "text-[#888] hover:text-white"}`} data-testid={`tb-sort-${s}`}>{s}</button>
          ))}
          <span className="ml-auto text-[#666]">{list.length} item{list.length === 1 ? "" : "s"}</span>
        </div>
        {tab === "monsters" && filterOpen && (
          <div className="grid grid-cols-3 gap-1" data-testid="tb-monster-filters">
            <Input value={crFilter} onChange={e => setCrFilter(e.target.value)} placeholder="CR" className="h-7 text-xs bg-[#1a1a1a] border-[#333]" data-testid="tb-filter-cr" />
            <Input value={typeFilter} onChange={e => setTypeFilter(e.target.value)} placeholder="Type" className="h-7 text-xs bg-[#1a1a1a] border-[#333]" data-testid="tb-filter-type" />
            <Input value={sizeFilter} onChange={e => setSizeFilter(e.target.value)} placeholder="Size" className="h-7 text-xs bg-[#1a1a1a] border-[#333]" data-testid="tb-filter-size" />
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto" data-testid="tb-list">
        {monstersLoading && tab === "monsters" && <p className="p-3 text-sm text-[#888]">Loading monsters…</p>}
        {!monstersLoading && list.length === 0 && (
          <p className="p-4 text-sm text-[#666] italic text-center">{search ? `No matches for “${search}”` : "Nothing here yet."}</p>
        )}
        {list.map((item, idx) => {
          const key = `${item._src}:${item.id}`;
          const isFav = favs.includes(key);
          const subtitle =
            item._src === "monster"
              ? [item.size, item.type || item.creature_type, item.alignment].filter(Boolean).join(" · ")
              : item._src === "character"
                ? [item.race, item.character_class, item.level ? `lvl ${item.level}` : null].filter(Boolean).join(" · ")
                : item._src === "npc"
                  ? [item.race, item.profession].filter(Boolean).join(" · ")
                  : item._src === "creature"
                    ? [item.size, item.creature_type, item.challenge_rating ? `CR ${item.challenge_rating}` : null].filter(Boolean).join(" · ")
                    : "";
          return (
            <div
              key={`${key}-${idx}`}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDoubleClick={() => quickSpawn(item)}
              className="flex items-center gap-2 px-3 py-2 border-b border-[#222] hover:bg-[#1a1a22] cursor-grab active:cursor-grabbing group"
              data-testid={`tb-item-${item.id}`}
            >
              {/* Circular portrait */}
              <div className="w-11 h-11 rounded-full bg-[#222] border border-[#333] overflow-hidden flex-shrink-0 flex items-center justify-center">
                {item.image_path || item.token_image ? (() => {
                  const raw = item.image_path || item.token_image;
                  const src = raw.startsWith("http")
                    ? raw
                    : raw.startsWith("/")
                      ? `${process.env.REACT_APP_BACKEND_URL}${raw}`
                      : `${process.env.REACT_APP_BACKEND_URL}/api/files/${raw}`;
                  return <img src={src} alt="" className="w-full h-full object-cover" crossOrigin="use-credentials" />;
                })() : (
                  <span className="text-xs font-bold text-[#666]">{(item.name || "?").slice(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{item.name}</p>
                {subtitle && <p className="text-xs text-[#888] truncate">{subtitle}</p>}
                {item._src === "monster" && item.challenge_rating !== undefined && item.challenge_rating !== null && (
                  <span className="text-xs text-amber-400">CR {cleanCR(item.challenge_rating)}</span>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); toggleFav(key); }}
                className={`p-1 transition-colors ${isFav ? "text-yellow-400" : "text-[#444] hover:text-yellow-400"}`}
                title={isFav ? "Remove favorite" : "Add favorite"}
                data-testid={`tb-fav-${item.id}`}
              >
                <Star className={`w-4 h-4 ${isFav ? "fill-yellow-400" : ""}`} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); quickSpawn(item); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-primary hover:text-white bg-primary/20 hover:bg-primary rounded-sm"
                title="Quick spawn at map centre"
                data-testid={`tb-quickspawn-${item.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-1.5 border-t border-[#333] bg-[#0a0a0d] text-xs text-[#666] flex items-center justify-between">
        <span>Drag to map · double-click = quick spawn · ★ favorite</span>
      </div>

      {/* Resize handle */}
      <div
        onPointerDown={startResize}
        className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize bg-transparent hover:bg-primary/30"
        data-testid="tb-resize"
      />
    </div>
  );
};

export default TokenBrowserPanel;
